<template>
  <g
    class="room-table"
    :transform="`translate(${localX}, ${localY}) rotate(${rotation})`"
    ref="roomTableRef"
  >
    <!--SHAPE SELECTION-->
    <!-- Invisible Interactive Layer -->
    <rect
      v-if="shape === TableShape.SQUARE"
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      v-bind="propsInvisibleShape"
      class="square-invisible"
    />
    <!-- Visible Shape -->
    <rect
      v-if="shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      v-bind="propsVisibleShape"
      class="square"
    />
    <!-- Red stripe overlay for square -->
    <rect
      v-if="stripeId && shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      :fill="stripe"
      pointer-events="none"
      class="square-striped"
    />

    <!-- Invisible Interactive Layer -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      :cx="0"
      :cy="0"
      :r="width"
      v-bind="propsInvisibleShape"
      class="circle-invisible"
    />
    <!-- Visible Shape -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      :cx="0"
      :cy="0"
      :r="width"
      v-bind="propsVisibleShape"
      class="circle"
    />
    <!-- Red stripe overlay for circle -->
    <circle
      v-if="stripeId && shape === TableShape.CIRCLE"
      :cx="0"
      :cy="0"
      :r="width"
      :fill="stripe"
      pointer-events="none"
      class="circle-striped"
    />

    <!-- Invisible Interactive Layer -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="`M-${width},-${height / 2} a1,1 0 0,0 ${width * 2},0`"
      v-bind="propsInvisibleShape"
      class="half-circle-invisible"
    />
    <!-- Visible Shape -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="`M-${width},-${height / 2} a1,1 0 0,0 ${width * 2},0`"
      v-bind="propsVisibleShape"
      class="half-circle"
    />
    <!-- Red stripe overlay for half-circle -->
    <path
      v-if="stripeId && shape === TableShape.HALF_CIRCLE"
      :d="`M-${width},-${height / 2} a1,1 0 0,0 ${width * 2},0`"
      :fill="stripe"
      pointer-events="none"
      class="half-circle-striped"
    />

    <!-- Invisible Interactive Layer -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsInvisibleShape"
      class="triangle-invisible"
    />
    <!-- Visible Shape -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsVisibleShape"
      class="triangle"
    />
    <!-- Red stripe overlay for triangle -->
    <polygon
      v-if="stripeId && shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      :fill="stripe"
      pointer-events="none"
      class="triangle-striped"
    />

    <PianoShape
      v-if="shape === TableShape.PIANO"
      :width="width"
      :height="height"
      :class="[strokeClass, fillClass, 'piano']"
    />
    <!-- Red stripe overlay for piano -->
    <PianoShape
      v-if="stripeId && shape === TableShape.PIANO"
      :width="width"
      :height="height"
      :fill="stripe"
      class="piano-striped"
      style="pointer-events: none"
    />

    <!--LABEL CONTENT SELECTION-->
    <g v-if="marker === 'xmark'" class="xmark">
      <line class="line-1 stroke-white" v-bind="xmarkLeftLine" :stroke-width="2 / venueScale" />
      <line class="line-2 stroke-white" v-bind="xmarkRightLine" :stroke-width="2 / venueScale" />
    </g>
    <text
      v-else-if="marker === 'checkmark'"
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      class="checkmark"
      :class="[textFillClass || 'fill-white']"
      style="font-size: 1.3px"
      :transform="`rotate(${-rotation})`"
    >
      &#10003;
    </text>
    <text
      v-else-if="!truncate"
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      :class="[textFillClass, 'label']"
      :style="{ fontSize: `${fontSize}px` }"
      :transform="`rotate(${-rotation})`"
    >
      <slot />
    </text>
    <foreignObject v-else :x="-width" :y="-height" :width="width * 2" :height="height * 2">
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        :class="[textFillClass, 'label flex items-center justify-center text-center w-full h-full']"
        :style="{ fontSize: `${fontSize}px`, padding: `0 ${1 / aspectRatio}px` }"
      >
        <slot />
      </div>
    </foreignObject>

    <!-- ICON SELECTION -->
    <g v-if="icon" :transform="`translate(${iconX}, ${iconY})`" style="isolation: isolate">
      <circle
        :r="ICON_RADIUS"
        class="fill-surface-primary stroke-linear-strong"
        :stroke-width="ICON_RADIUS * 0.1"
      />
      <TableIcon
        :name="icon"
        :width="ICON_RADIUS * 1.4"
        :height="ICON_RADIUS * 1.4"
        :x="ICON_RADIUS * -0.7"
        :y="ICON_RADIUS * -0.7"
        class="text-fg-primary"
      />
    </g>
  </g>
</template>

<script lang="ts" setup>
import { useDraggable } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { ComputedRef, SVGAttributes, computed, inject, ref, toRefs, watch } from 'vue'

import PianoShape from '@/common/components/atoms/PianoShape.vue'
import TableIcon from '@/common/components/atoms/TableIcon.vue'
import { TableIconName } from '@/common/interfaces/TableIconInterfaces'
import { useSettingStore } from '@/stores/setting'
import { ROOM_TABLE_RELATIVE_WIDTH } from '@/utils/utils'

import { PerformanceTableRead, TableShape, TableShapeType } from '@generated/types'

export type Marker = 'checkmark' | 'xmark' | 'none'
export interface RoomTableProps {
  shape: TableShapeType
  width: number
  height: number
  x: number
  y: number
  rotation?: number
  marker?: Marker
  fillClass?: string
  strokeClass?: string
  textFillClass?: string
  draggable?: boolean
  focusable?: boolean
  isFocused?: boolean
  ariaLabel?: string
  truncate?: boolean
  stripeId?: string
  fontSize?: number
  icon?: TableIconName
}

const props = withDefaults(defineProps<RoomTableProps>(), {
  rotation: 0,
  marker: 'none',
  fillClass: 'fill-surface-table-available',
  strokeClass: 'stroke-transparent',
  textFillClass: 'fill-fg-table-available',
  draggable: false,
  focusable: false,
  isFocused: false,
  ariaLabel: '',
  truncate: false,
  stripeId: undefined,
  fontSize: 1.3,
  icon: undefined,
})
const { width, height, shape, draggable, strokeClass, fillClass, focusable, isFocused, ariaLabel } =
  toRefs(props)
const { ratio: aspectRatio } = storeToRefs(useSettingStore())
const stripeId = computed<string | undefined>(() =>
  props.stripeId ? _.kebabCase(props.stripeId) : undefined,
)
const stripe = computed<string | undefined>(() =>
  props.stripeId ? `url(#${props.stripeId})` : undefined,
)

const trianglePoints = computed<string>(function () {
  return `${-width.value * 1.5},${height.value * 0.5} ${width.value * 0.5},${height.value * 0.5} ${
    width.value * 0.5
  },${-height.value * 1.5} ${-width.value * 1.5},${height.value * 0.5}`
})
const xmarkMargin = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      return width.value * 0.4
    case TableShape.HALF_CIRCLE:
      return width.value * 0.7
    default:
      return 0.6
  }
})
const xmarkLeftLine = computed<SVGAttributes>(function () {
  if (shape.value === TableShape.CIRCLE)
    return {
      x1: -width.value + xmarkMargin.value,
      y1: -width.value + xmarkMargin.value,
      x2: width.value - xmarkMargin.value,
      y2: width.value - xmarkMargin.value,
    }

  return {
    x1: -width.value + xmarkMargin.value,
    y1: -height.value + xmarkMargin.value,
    x2: width.value - xmarkMargin.value,
    y2: height.value - xmarkMargin.value,
  }
})
const xmarkRightLine = computed<SVGAttributes>(function () {
  if (shape.value === TableShape.CIRCLE)
    return {
      x1: -width.value + xmarkMargin.value,
      y1: width.value - xmarkMargin.value,
      x2: width.value - xmarkMargin.value,
      y2: -width.value + xmarkMargin.value,
    }

  return {
    x1: -width.value + xmarkMargin.value,
    y1: height.value - xmarkMargin.value,
    x2: width.value - xmarkMargin.value,
    y2: -height.value + xmarkMargin.value,
  }
})

// ICON POSITIONING
const ICON_RADIUS = 0.9
const iconX = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      // For circle: position just outside circle at top-right
      return width.value * Math.cos(-Math.PI / 4) + ICON_RADIUS * 0.5
    case TableShape.HALF_CIRCLE:
      // For half-circle: right edge at top
      return width.value
    case TableShape.TRIANGLE:
      // For triangle: top-right corner is at (width * 0.5, -height * 1.5)
      return width.value * 0.5
    case TableShape.PIANO:
      // For piano: right edge
      return width.value
    default:
      // For square and rectangle
      return width.value
  }
})
const iconY = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      // For circle: position just outside circle at top-right
      return width.value * Math.sin(-Math.PI / 4) - ICON_RADIUS * 0.5
    case TableShape.HALF_CIRCLE:
      // For half-circle: top of the arc
      return -height.value / 2
    case TableShape.TRIANGLE:
      // For triangle: top-right corner is at (width * 0.5, -height * 1.5)
      return -height.value * 1.5
    case TableShape.PIANO:
      // For piano: top edge
      return -height.value
    default:
      // For square and rectangle
      return -height.value
  }
})

// D R A G G I N G    F U N C T I O N A L I T Y
const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:position', values: Pick<PerformanceTableRead, 'x' | 'y'>): void
}>()
const localX = ref(props.x)
const localY = ref(props.y)
watch([() => props.x, () => props.y], ([x, y]) => {
  localX.value = x
  localY.value = y
})
let mouseToCenterOffset: { xDistance: number; yDistance: number } = { xDistance: 0, yDistance: 0 }
const roomTableRef = ref<SVGGraphicsElement | null>(null)
const svgElementRef = inject('svgElementRef') as ComputedRef<SVGElement | null>
const venueRoomPaddingX = inject('paddingX') as ComputedRef<number>
const venueScale = inject('venueScale') as ComputedRef<number>
const svgRelativeWidth = inject('svgRelativeWidth') as ComputedRef<number> | undefined
const svgRelativeHeight = inject('svgRelativeHeight') as ComputedRef<number> | undefined
useDraggable(roomTableRef, {
  draggingElement: svgElementRef,
  containerElement: svgElementRef,
  onStart(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getMouseRelativePosition(offsetX, offsetY)
    mouseToCenterOffset = { xDistance: x - localX.value, yDistance: y - localY.value }
  },
  onMove(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    localX.value = x
    localY.value = y
  },
  onEnd(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    emit('update:position', { x: Math.round(x), y: Math.round(y) })
  },
})
function getMouseRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  return {
    x: (offsetX - venueRoomPaddingX.value) / venueScale.value,
    y: offsetY / venueScale.value,
  }
}
function getTableRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  const currentSvgRelativeWidth = svgRelativeWidth?.value ?? ROOM_TABLE_RELATIVE_WIDTH
  const currentSvgRelativeHeight =
    svgRelativeHeight?.value ?? ROOM_TABLE_RELATIVE_WIDTH / aspectRatio.value
  const { x: relativeMouseX, y: relativeMouseY } = getMouseRelativePosition(offsetX, offsetY)
  return {
    x: _.clamp(relativeMouseX - mouseToCenterOffset.xDistance, 0, currentSvgRelativeWidth),
    y: _.clamp(relativeMouseY - mouseToCenterOffset.yDistance, 0, currentSvgRelativeHeight),
  }
}

const STROKE_WIDTH = 2
const propsVisibleShape = computed<SVGAttributes>(() => ({
  'stroke-width': STROKE_WIDTH / venueScale.value,
  'stroke-linejoin': 'round',
  class: [strokeClass.value, fillClass.value],
  ...(focusable.value && {
    role: 'radio',
    tabindex: '-1',
    'aria-checked': false,
    'aria-label': ariaLabel.value,
  }),
}))

const STROKE_TARGET_AREA = 6
const propsInvisibleShape = computed<SVGAttributes>(() => ({
  'stroke-width': STROKE_TARGET_AREA / venueScale.value,
  'stroke-linejoin': 'round',
  class: focusable.value && isFocused.value ? 'stroke-fg-g-primary' : 'stroke-transparent',
  fill: 'transparent',
}))
</script>
